import { useTheme } from '@doccareplus/shared/lib/theme';
import type { Theme } from '@doccareplus/shared/lib/theme';
import { cn } from '@doccareplus/shared/lib/utils';

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

/**
 * Compact icon button that toggles between light and dark.
 * Suited for chrome-free surfaces such as the auth pages.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className={cn(
        'flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)] cursor-pointer',
        className
      )}
    >
      {isDark ? <MoonIcon className="h-[17px] w-[17px]" /> : <SunIcon className="h-[17px] w-[17px]" />}
    </button>
  );
}

const THEME_OPTIONS: { value: Theme; label: string; icon: (props: { className?: string }) => React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'system', label: 'Auto', icon: MonitorIcon },
];

/**
 * Three-way segmented control (Light / Dark / Auto).
 * Suited for menus and settings panels such as the appbar profile dropdown.
 */
export function ThemeSegmentedControl({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'flex items-center gap-0.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface-2)] p-0.5',
        className
      )}
    >
      {THEME_OPTIONS.map((option) => {
        const isActive = theme === option.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={option.label}
            title={option.label}
            onClick={() => setTheme(option.value)}
            className={cn(
              'flex min-w-0 flex-1 items-center justify-center gap-1 rounded-[7px] px-1 py-1.5 text-[11.5px] font-semibold transition-colors cursor-pointer',
              isActive
                ? 'bg-[var(--surface)] text-[var(--text)] shadow-[var(--shadow-1)]'
                : 'text-[var(--text-3)] hover:text-[var(--text-2)]'
            )}
          >
            <Icon className="h-[14px] w-[14px] shrink-0" />
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
